import React from "react";
import { motion } from "framer-motion";
import {
  BriefcaseIcon,
  ClipboardDocumentCheckIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  AcademicCapIcon,
  CheckBadgeIcon,
} from "@heroicons/react/24/outline";

const workflowSteps = [
  {
    icon: BriefcaseIcon,
    title: "Onboarding",
    desc: "Smooth, paperless joining.",
    color: "bg-rose-100 text-rose-600",
  },
  {
    icon: UserGroupIcon,
    title: "Attendance",
    desc: "Geo & biometric tracking.",
    color: "bg-emerald-100 text-emerald-600",
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: "Leave",
    desc: "Fast approvals & insights.",
    color: "bg-amber-100 text-amber-600",
  },
  {
    icon: CurrencyDollarIcon,
    title: "Payroll",
    desc: "Accurate monthly salary.",
    color: "bg-purple-100 text-purple-600",
  },
  {
    icon: AcademicCapIcon,
    title: "Training",
    desc: "Skill-based learning.",
    color: "bg-pink-100 text-pink-600",
  },
  {
    icon: CheckBadgeIcon,
    title: "Performance",
    desc: "Goals & appraisal cycles.",
    color: "bg-indigo-100 text-indigo-600",
  },
];

const WorkflowStrip = () => {
  return (
    <section
      className="py-20 bg-gray-50"
      style={{ fontFamily: "Poppins, Roboto, sans-serif" }}
    >
      {/* Heading */}
      <div className="max-w-7xl mx-auto px-6 text-center mb-12">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-3xl sm:text-4xl font-extrabold text-gray-900"
        >
          Seamless Employee <span className="text-teal-600">Workflow</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="mt-3 text-gray-600 text-sm sm:text-base max-w-2xl mx-auto"
          style={{ fontFamily: "Roboto, sans-serif" }}
        >
          End-to-end HR process designed for productivity and employee delight.
        </motion.p>
      </div>

      {/* Auto-scroll slider container */}
      <div className="overflow-hidden">
        {/* Auto-moving flex container */}
        <motion.div
          className="flex gap-8 w-max px-6 py-2"
          animate={{ x: ["0%", "-50%"] }}
          transition={{
            repeat: Infinity,
            duration: 25,
            ease: "linear",
          }}
        >
          {/* Duplicate list ×2 for infinite loop */}
          {[...workflowSteps, ...workflowSteps].map((step, index) => {
            const Icon = step.icon;

            return (
              <motion.div
                key={index}
                className="flex flex-col items-center w-48 sm:w-56 bg-white rounded-2xl shadow-md border border-gray-100 p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                <div
                  className={`w-16 h-16 flex items-center justify-center rounded-xl shadow-sm ${step.color}`}
                >
                  <Icon className="w-9 h-9" />
                </div>

                <h3
                  className="mt-4 text-lg font-semibold text-gray-900"
                  style={{ fontFamily: "Poppins, sans-serif" }}
                >
                  {step.title}
                </h3>

                <p
                  className="mt-1 text-gray-600 text-xs sm:text-sm text-center"
                  style={{ fontFamily: "Roboto, sans-serif" }}
                >
                  {step.desc}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default WorkflowStrip;
